import { useState } from "react";
import { Clock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useDispatch, useSelector } from "react-redux";
import { diffWords } from "diff";

import {
	IKeyTermMitigations,
	IMitigation,
	MitigationType,
	undoProcessSuggestion,
} from "../../store/playbook-analysis-slice";
import { customDocumentSearch } from "../../utils/addinUtils";
import { usePlaybook } from "../../utils/playbook";
import { RootState } from "../../store/store";
import { lastChangedAt } from "../lib/last-changed-at";

import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { cn } from "../lib/utils";

interface IProcessedCard {
	mitigationIdx: number;
	keyTermIdx: number;
}

export function ProcessedCard({ keyTermIdx, mitigationIdx }: IProcessedCard) {
	const { trackChanges } = usePlaybook();
	const dispatch = useDispatch();

	const [loading, setLoading] = useState<boolean>();

	const keyTerm: IKeyTermMitigations = useSelector((state: RootState) => state.analysis.processed.risks[keyTermIdx]);
	const mitigation = keyTerm.values[mitigationIdx];

	async function handleTrackChange(mitigation: IMitigation) {
		setLoading(true);
		let searchResponse = false;

		if (mitigation.comparison_type === MitigationType.MISSING) {
			searchResponse = await customDocumentSearch({
				action: "accept",
				reference: keyTerm.values.at(0).standard_text.at(0),
				html_version: "<div></div>",
				comment: mitigation.rationale,
			});
		} else if (mitigation.comparison_type === MitigationType.CONTRADICTORY) {
			const diff = diffWords(mitigation.reference, mitigation.new_version);

			const stringToSearch = diff.map((text) => text.value).join(" ");

			searchResponse = await customDocumentSearch({
				action: "accept",
				reference: stringToSearch,
				html_version: mitigation.reference,
				comment: mitigation.rationale,
			});
		}

		if (searchResponse) {
			const response = await trackChanges(null, mitigation.risk_id);

			if (!response.success) {
				throw response;
			}

			dispatch(
				undoProcessSuggestion({
					type: mitigation.comparison_type,
					keyTermIdx,
					mitigationIdx,
				})
			);
			toast.success(`Undo Successfull`);
		} else {
			toast.error("Something Went Wrong");
		}

		setLoading(false);
	}

	return (
		<Card className="mt-3 rounded-xl py-4">
			<CardContent className="flex gap-3 flex-col px-4">
				<p className="text-xs font-normal">
					{mitigation.diff &&
						mitigation.diff.map((text, diffIdx: number) => (
							<span
								key={diffIdx}
								style={{
									textDecoration: text.removed ? "line-through" : "none",
									color: text.removed ? "#dc2626" : text.added ? "#16a34a" : "black",
									fontSize: "0.75rem",
								}}
							>
								{text.value}
							</span>
						))}
				</p>

				<div className="flex justify-between items-center">
					<Badge variant="outline" className="flex gap-1 text-gray-400">
						<Clock />
						{lastChangedAt(mitigation.updated_at)}
					</Badge>
					<Badge className={cn(mitigation.accepted ? "bg-green-50 text-green-500" : "bg-yellow-50 text-yellow-700")}>
						{mitigation.accepted ? "Accepted" : "Rejected"}
					</Badge>
				</div>

				<Button
					variant="outline"
					className="border-primary text-primary hover:text-primary cursor-pointer"
					onClick={async () => await handleTrackChange(mitigation)}
				>
					{loading ? (
						<div className="flex items-center justify-center">
							<Loader2 className="size-4 animate-spin" />
						</div>
					) : (
						"Undo"
					)}
				</Button>
			</CardContent>
		</Card>
	);
}
